import { SIGNED_URL_EXPIRED_TIME } from 'constants/params';
import { api, ApiRes } from './_api-client';
import { uploadFileService } from './file-flow.service';
import { SetAccessedAtRes } from './project.service';

export enum ConfigFlags {
  reverse,
  flip,
  stabilization,
  brightness,
  contrast,
  volume,
  highlightSensitivity,
}

export enum RemoveType {
  trash,
  immediately,
}

export enum FileType {
  unknown,
  video,
  audio,
  image,
}

export enum FileStatus {
  None,
  PendingUpload,
  UploadingToAWS,
  PendingProcessing,
  UploadingToProcessingServer,
  Processing,
  Processed,
  Error,
  Available,
  PendingProcessingEffects,
  PendingChangingMediaOriginDate,
  PendingDownloadingFromGoogle,
  UploadInterrupted,
}

export enum Rotate {
  default,
  d90,
  d180,
  d270,
}

export interface FileConfigVal {
  brightness: number;
  contrast: number;
  volume: number;
  highlightSensitivity: number;
  rotate: Rotate | null;
}

export interface FileConfig extends FileConfigVal {
  flags: ConfigFlags[];
}

export interface FileUrls {
  posterUrl: Url | null;
  fileUrl: Url | null;
  mediumQualityUrl: Url | null;
  lowQualityUrl: Url | null;
  reverseMediumQualityUrl: Url | null;
  reverseLowQualityUrl: Url | null;
  stabilizationMediumQualityUrl: Url | null;
  stabilizationLowQualityUrl: Url | null;
  reverseWithStabilizationMediumQualityUrl: Url | null;
  reverseWithStabilizationLowQualityUrl: Url | null;
}

export interface ProjectFile {
  id: number;
  accessedAt: string | null;
  createdAt: string;
  updatedAt: string;
  mediaOriginDate: string | null;
  fileName: string;
  fileType: FileType;
  folderId: FolderId | null;
  duration: number | null;
  size: number | null;
  width: number | null;
  height: number | null;
  origBitRate: number | null;
  bitRate: number | null;
  frameRate: number | null;
  status: FileStatus;
  fileWithEffectsUrls: FileUrls;
  config: FileConfig;
}

export interface FontData {
  value: number;
  name: string;
  path: string;
}

export interface SetFilePosterBody {
  fileId: FileId;
  posterKey: string;
}

export interface SetFileConfig extends FileConfig {
  fileId: FileId;
  projectId: ProjectId;
}

export interface SetFilePosterRes extends ApiRes {
  data: Url;
}

export interface SetFileConfigRes extends ApiRes {
  isProcessing: boolean;
}

export interface SetFileMediaOriginDateBody {
  mediaFileId: FileId;
  mediaOriginDate: string;
}

export interface UploadPoster {
  projectId: ProjectId;
  key: string;
}

export interface UploadTextImage {
  projectId: ProjectId;
  storyId: StoryId;
  key: 'title' | 'credits';
}

export interface ProcessFileBody extends UploadPoster {
  projectId: ProjectId;
  key: string;
  reUpload: boolean;
  google?: {
    url: Url;
    token: string;
  };
}

export interface UploadClipThumbnail extends UploadPoster {
  storyId: StoryId;
  highlightId?: number;
}

export interface UploadFileResponseDto {
  url: string;
  file: ProjectFile;
}

/* S E R V I C E */

export const removeFile = (fileId: FileId) => {
  return api.delete<ApiRes>(`/MediaFile/RemoveFile?fileId=${fileId}&removeType=${RemoveType.immediately}`);
};

export const getProjectFiles = (projectId: ProjectId) => {
  return api.get<ProjectFile[]>(
    `/MediaFile/GetProjectFilesWithConfig?projectId=${projectId}&expires=${SIGNED_URL_EXPIRED_TIME}`
  );
};

export const GetAvailableFonts = () => {
  return api.get<FontData[]>(`/MediaFile/GetAvailableFonts`);
};

export const setFilePoster = (body: SetFilePosterBody) => {
  return api.patch<SetFilePosterRes>(`/MediaFile/SetFilePoster`, body);
};

export const setFileConfig = (body: SetFileConfig) => {
  return api.put<SetFileConfigRes>(`/MediaFile/SetFileConfig`, body);
};

export const GetAvailableFileExtensions = (fileType: FileType) => {
  return api.get<string[]>(`/MediaFile/GetAvailableFileExtensions?fileType=${fileType}`);
};

export const anyProcessingFiles = () => {
  return api.get<boolean>(`/MediaFile/AnyProcessingFiles`);
};

export const setFileAccessedAt = (fileId: FileId) => {
  return api.patch<SetAccessedAtRes>(`/MediaFile/SetFileAccessedAt?fileId=${fileId}`);
};

export const setFileMediaOriginDate = (body: SetFileMediaOriginDateBody) => {
  return api.patch<SetAccessedAtRes>(`/MediaFile/SetFileMediaOriginDate`, body);
};

export const changeStatus = (fileId: number) => {
  return api.patch<ProjectFile>(`/MediaFile/ChangeStatus?fileId=${fileId}`);
};

/* uploads */

export const uploadFile = (body: ProcessFileBody, fileSize: number) => {
  return uploadQuery('UploadFile', body, fileSize);
};

/** Remint a fresh S3 PUT URL for an existing PendingUpload row (upload queue slot start). */
export const refreshUploadUrl = (fileId: number, fileSize: number) => {
  return api.post<UploadFileResponseDto>(
    `/MediaFile/RefreshUploadUrl?fileId=${fileId}`,
    {},
    {
      headers: { 'X-Content-Length': fileSize },
    }
  );
};

export const uploadClipThumbnail = async (body: UploadClipThumbnail, blob: Blob) => {
  return uploadImage('UploadClipThumbnail', body, blob);
};

export const uploadFilePoster = async (body: UploadPoster, blob: Blob) => {
  return uploadImage('UploadFilePoster', body, blob);
};

export const uploadTextImage = async (body: UploadTextImage, blob: Blob) => {
  return uploadImage('UploadTextImage', body, blob);
};

const uploadImage = async <T extends UploadPoster>(route: string, body: T, blob: Blob) => {
  const result = await uploadQuery(route, body, blob.size);
  await uploadFileService({
    url: result.data.url,
    file: new File([blob], body.key, { type: blob.type }),
    fileSize: blob.size,
  });
  return result.data.url.split('?')[0];
};

const uploadQuery = <T extends UploadPoster>(route: string, body: T, fileSize: number) => {
  return api.post<UploadFileResponseDto>(`/MediaFile/${route}`, body, {
    headers: { 'X-Content-Length': fileSize },
  });
};
